import {ReactNode, useState, ChangeEvent} from 'react';
import {TextWithIcon} from '@legit.health/ui';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/Warning';
import {styled} from '@mui/material';
import {Box, Button, Container, Grid, Typography} from '@mui/material';
import {TFunction} from 'i18next';

const StyledVideo = styled('video')({
  width: '100%'
});

const StyledVideoInput = styled('input')({
  display: 'none'
});

type Props = {
  rightColumnInitialComponent?: ReactNode | null;
  onVideoReady: (video: Blob) => void;
  onClearVideo: () => void;
  t: TFunction;
};

function VideoUploader({rightColumnInitialComponent, onVideoReady, onClearVideo, t}: Props) {
  const [uploadVideo, setUploadVideo] = useState<string | null>(null);

  function handleVideoUploaded(e: ChangeEvent<any>): void {
    const videoUploaded = URL.createObjectURL(e.target.files[0]);
    if (uploadVideo !== null) {
      onClearVideo();
      setUploadVideo(null);
    }
    setUploadVideo(videoUploaded);
    const blob = new Blob(e.target.files, {type: 'video/mp4'});
    onVideoReady(blob);
  }

  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          <Box>
            <TextWithIcon
              variant="h3"
              iconLeft={<WarningIcon color="error" />}
              sx={{
                mb: 2,
                justifyContent: 'center'
              }}
            >
              {t('shared/common:diagnosticReport.form.video.cannotAccessCam')}
            </TextWithIcon>
            <Typography
              variant="body1"
              align="center"
              sx={(theme) => ({marginBottom: theme.spacing(2)})}
            >
              {t('shared/common:diagnosticReport.form.video.uploadVideoManually')}
            </Typography>
            {uploadVideo && <StyledVideo src={uploadVideo} controls muted />}
          </Box>
          <StyledVideoInput
            accept="video/*"
            id="uploadVideo"
            type="file"
            onChange={handleVideoUploaded}
          />
          <label htmlFor="uploadVideo">
            <Button
              variant="contained"
              color="secondary"
              component="span"
              disableElevation
              fullWidth
              startIcon={uploadVideo ? <AutorenewIcon /> : <CloudUploadIcon />}
            >
              {uploadVideo
                ? t('shared/common:diagnosticReport.form.video.dismiss')
                : t('shared/common:diagnosticReport.form.video.uploadVideo')}
            </Button>
          </label>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          {rightColumnInitialComponent}
        </Grid>
      </Grid>
    </Container>
  );
}

export default VideoUploader;
